import { Box, Button, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { DateTime } from "luxon";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { green } from "@mui/material/colors";

import { convertTimezoneFromObj } from "../../../functions/helperFunctions";
import { useSelector } from "react-redux";

const ContractorSignerTab = ({
  selectedRow,
  fiveMinBeforeSession,
  setFiveMinBeforeSession,
  setRonScheduleOpen,
  isOwnerSign,
}) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession].user);
  const [ronDate, setRonDate] = useState("");

  const { contractor: mainContractor } =
    selectedRow?.processing_permit?.notarization || {};
  const { owner } = selectedRow?.processing_permit?.notarization || {};
  const { signing_order } = selectedRow?.processing_permit?.notarization || {};
  const { notarization } = selectedRow?.processing_permit || {};

  // Check if current user is an additional contractor
  const getCurrentUserAdditionalContractor = () => {
    const userEmail = (
      currentUser?.subuser?.email ||
      currentUser?.user?.email ||
      currentUser?.email ||
      ""
    ).toLowerCase();

    if (!userEmail) return null;

    const additionalContractors =
      selectedRow?.processing_permit?.notarization?.additional_contractors ||
      [];

    return additionalContractors.find(
      (ac) => ac?.email?.toLowerCase() === userEmail,
    );
  };

  const additionalContractorData = getCurrentUserAdditionalContractor();

  // Use additional contractor data if current user is one, otherwise use main contractor
  const contractor = additionalContractorData || mainContractor;

  const isRonScheduled = useCallback(() => {
    // Use the appropriate contractor's notarization (additional or main)
    const contractorNotarization = contractor?.notarization;

    return (
      contractorNotarization?.appointments?.booked?.length > 0 &&
      contractorNotarization?.has_notary === true &&
      contractorNotarization?.is_notarized === false &&
      contractorNotarization?.waiting_to_schedule === false
    );
  }, [contractor?.notarization]);

  useEffect(() => {
    // Use the appropriate contractor's notarization (additional or main)
    const contractorNotarization = contractor?.notarization;
    if (isRonScheduled()) {
      const { year, month, day_of_month, time } =
        contractorNotarization?.appointments?.booked[0];
      const userZone = currentUser?.subuser
        ? currentUser?.subuser?.timezone
        : currentUser?.user?.personal?.[0]?.timezone;

      const dateString = convertTimezoneFromObj(
        contractorNotarization?.appointments?.booked[0],
        userZone || "America/New_York",
        "cccc, LLLL dd, yyyy hh:mm a",
      );

      setRonDate(dateString);

      // Convert isoString to Date object
      const ronnDate = DateTime.utc(
        year,
        month,
        day_of_month,
        parseInt(time?.split(":")[0]),
        parseInt(time?.split(":")[1]),
      );

      const checkTimeDifference = () => {
        // Get the current time in user's timezone
        const nowUTC = DateTime.utc();

        // Calculate the difference in milliseconds
        const timeDifference = ronnDate - nowUTC;

        // Check if the difference is less than 5 minutes (300,000 milliseconds)
        if (timeDifference <= 600000) {
          setFiveMinBeforeSession(true);
        } else {
          setFiveMinBeforeSession(false);
        }
      };
      // Perform the initial check
      checkTimeDifference();

      const intervalId = setInterval(checkTimeDifference, 30000);

      // Clear the interval when the component unmounts or dependencies change
      return () => clearInterval(intervalId);
    } else {
      setRonDate("N/A");
      setFiveMinBeforeSession(false);
    }
  }, [
    contractor?.notarization,
    currentUser?.subuser,
    currentUser?.user?.personal,
    isRonScheduled,
    setFiveMinBeforeSession,
  ]);

  return (
    <Box>
      {signing_order === "owner" &&
      !owner?.notarization?.is_notarized &&
      !(
        owner?.notarization?.only_sign === true &&
        owner?.notarization?.is_signed === true
      ) ? (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography
              variant="body2"
              color="primary"
              fontWeight="bold"
              px={2}
              align="center"
            >
              Please wait for the owner's/agent's notarization/signing to
              complete.
            </Typography>
          </Box>
        </>
      ) : contractor?.notarization?.waiting_to_schedule &&
        !contractor?.notarization?.only_sign ? (
        <Box
          mt={contractor?.notarization?.notary_failed ? 1 : 3}
          align="center"
        >
          {contractor?.notarization?.notary_failed && (
            <Typography
              variant="h6"
              color="error"
              fontWeight="bold"
              px={2}
              pb={2}
              align="center"
            >
              Your Remote Online Notarization (RON) session has been canceled.
              Please reschedule.
            </Typography>
          )}
          <Button
            onClick={() => {
              setRonScheduleOpen(true);
            }}
            size="small"
            variant="contained"
          >
            Schedule your RON Session
          </Button>
        </Box>
      ) : contractor?.notarization?.only_sign &&
        !contractor?.notarization?.is_signed ? (
        <Box mt={3} align="center">
          <Typography variant="h6" px={1} color="primary" mb={2}>
            Your documents are ready and only require your signature
          </Typography>
          <Button
            onClick={() => {
              window.open(
                contractor?.notarization?.bn_session?.signer_url,
                "_blank",
              );
            }}
            size="small"
            variant="contained"
          >
            Sign Your Documents
          </Button>
        </Box>
      ) : contractor?.notarization?.is_ipen &&
        contractor?.notarization?.has_notary &&
        !contractor?.notarization?.is_notarized ? (
        <>
          <Typography align="center" variant="h6" color="primary" mb={2}>
            IPEN Session Scheduled
          </Typography>

          {contractor?.notarization?.require_ipen_pin ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                mt: 2,
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                mb={1}
              >
                Your IPEN Verification PIN:
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  variant="h4"
                  component="div"
                  align="center"
                  color="primary"
                  fontWeight="bold"
                  sx={{
                    letterSpacing: "0.5rem",
                    fontFamily: "monospace",
                    backgroundColor: "#f0f8ff",
                    border: "2px solid",
                    borderColor: "primary.main",
                    borderRadius: 1,
                    px: 3,
                    py: 1.5,
                  }}
                >
                  {contractor?.notarization?.ipen_pin || "----"}
                </Typography>
              </Box>
              <Typography
                variant="body2"
                align="center"
                color="text.secondary"
                px={2}
                sx={{ maxWidth: "400px" }}
              >
                Please provide this PIN to your assigned notary when you meet in
                person. The notary will use it to verify your identity and
                initiate the session.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                mt: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  backgroundColor: "#f0fff4",
                  border: "2px solid",
                  borderColor: "success.main",
                  borderRadius: 1,
                  px: 3,
                  py: 1.5,
                  mb: 2,
                }}
              >
                <CheckCircleOutlineIcon color="success" />
                <Typography variant="h6" color="success.main" fontWeight="bold">
                  No PIN Required
                </Typography>
              </Box>
              <Typography
                variant="body2"
                align="center"
                color="text.secondary"
                px={2}
                sx={{ maxWidth: "400px" }}
              >
                Your notary has everything they need to begin the session. They
                will contact you directly to initiate your IPEN appointment.
              </Typography>
            </Box>
          )}
        </>
      ) : isRonScheduled() ? (
        <>
          <Typography
            align="center"
            variant="h6"
            color={fiveMinBeforeSession ? "error" : "primary"}
          >
            {fiveMinBeforeSession
              ? "RON Session is Started!"
              : "RON Session is Scheduled at:"}
          </Typography>
          <Typography
            align="center"
            variant="body2"
            fontWeight="bold"
            color="secondary"
          >
            {ronDate}
          </Typography>
          <Box align="center" mt={2}>
            {fiveMinBeforeSession ? (
              <Button
                variant="contained"
                onClick={() => {
                  window.open(
                    selectedRow?.processing_permit?.notarization?.contractor
                      ?.notarization?.bn_session?.signer_url,
                    "_blank",
                  );
                }}
              >
                Join Session
              </Button>
            ) : (
              <Typography px={2}>
                It is recommended that you join the session five minutes before
                it starts in order to allow enough time for biometric
                authentication.
              </Typography>
            )}
          </Box>
        </>
      ) : contractor?.notarization?.is_notarized ? (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              color="primary"
              align="center"
              fontWeight="bold"
              px={1}
              mt={1}
            >
              {!contractor?.notarization?.is_notarized
                ? "Notarization is in progress"
                : "Notarization is completed"}
            </Typography>
            {contractor?.notarization?.is_notarized && (
              <CheckCircleIcon
                style={{
                  color: green[500],
                  paddingTop: 4,
                  fontSize: "30px",
                }}
              />
            )}
          </Box>
          <Typography
            px={2}
            mt={2}
            align="center"
            variant="body2"
            fontWeight="bold"
            color="primary"
          >
            {!notarization?.finalized
              ? "Your documents have been notarized successfully. In order to access your final documents, please wait until the owner has completed the RON session as well."
              : "The notarization of your documents has been completed. "}
          </Typography>
        </>
      ) : contractor?.notarization?.only_sign &&
        contractor?.notarization?.is_signed ? (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              color="primary"
              align="center"
              fontWeight="bold"
              px={1}
              mt={1}
            >
              Signed Documents!
            </Typography>

            <CheckCircleIcon
              style={{
                color: green[500],
                paddingTop: 4,
                fontSize: "30px",
              }}
            />
          </Box>
          <Typography
            px={2}
            mt={2}
            align="center"
            variant="body2"
            fontWeight="bold"
            color="primary"
          >
            {isOwnerSign
              ? "Your documents have been signed successfully. In order to access your final documents, please wait until the owner has completed the RON session as well."
              : "Your documents have been signed successfully."}
          </Typography>
        </>
      ) : (
        <></>
      )}
    </Box>
  );
};

export default ContractorSignerTab;
